<template>
  <div class="cta-contact-person" ref="ctaContactPersonElement">
    <component
      v-for="module in data.contentModules"
      :key="module.id"
      :is="ContentModules[module.__typename]"
      :data="module"
      style="--background: var(--color-black)"
    ></component>
    <div ref="personElement" class="person">
      <div class="row">
        <p class="name">
          <b>{{ data.teamMember?.firstname }} {{ data.teamMember?.lastname }}</b>
        </p>
        <p v-if="serviceName" class="services">
          {{ serviceName }}
        </p>
        <p class="role" v-else-if="data.teamMember?.role">
          {{ data.teamMember.role }}
        </p>
        <div class="links">
          <a
            v-if="data.teamMember?.eMail"
            class="link email"
            target="_blank"
            rel="noopener noreferrer"
            :href="`mailto:${data.teamMember.eMail}`"
            >{{ data.teamMember.eMail }}</a
          >
          <a
            v-if="data.teamMember?.phone"
            class="link phone"
            target="_blank"
            rel="noopener noreferrer"
            :href="`tel:${data.teamMember.phone}`"
            >{{ data.teamMember.phone }}</a
          >
          <a
            v-if="data.teamMember?.videocallLink"
            class="link videocall"
            target="_blank"
            rel="noopener noreferrer"
            :href="`${data.teamMember.videocallLink}`"
            >{{ general?.makeAppointment }}</a
          >
        </div>
      </div>
      <div class="row">
        <DatoAsset
          :lazy-load="true"
          fade-in="on-intersection"
          class="portrait -vl"
          v-if="data.teamMember?.imageCta"
          aspect-ratio="4/2"
          :asset="data.teamMember.imageCta as DatoAssetDto"
          sizes="45w"
        />
        <DatoAsset
          :lazy-load="true"
          fade-in="on-intersection"
          class="portrait -vs"
          v-if="data.teamMember?.imageVs"
          aspect-ratio="4/5"
          :asset="data.teamMember.imageVs as DatoAssetDto"
          sizes="88w"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useTextFadeIn from '~/utils/useTextFadeIn';
import type { CtaModuleFragment } from '#gql';
import type { GetGeneralQuery } from '#gql';
import DatoAsset from '~/dato/DatoAsset.vue';
import type { DatoAssetDto } from '~/dato/dato';
import { ContentModules } from '~/components/content-modules/ContentModules';

const props = defineProps<{
  serviceName: string;
  data: CtaModuleFragment;
}>();

const { data: dataGeneral } = await useAsyncGql({
  operation: 'GetGeneral'
});

const general = (dataGeneral.value as GetGeneralQuery).general;

const ctaContactPersonElement = ref<null | HTMLElement>(null);
const titleElement = ref<null | HTMLElement>(null);
const textfieldsElement = ref<null | HTMLElement>(null);
// const personElement = ref<null | HTMLElement>(null);

// useTextFadeIn(ctaContactPersonElement, [titleElement, textfieldsElement, personElement]);
</script>

<style scoped lang="scss">
.cta-contact-person {
  background-color: var(--color-black);
  color: var(--color-white);

  * {
    position: relative;
  }

  > .person {
    @extend %base-grid;

    border-bottom: 1px solid var(--color-black); // fix white border above footer on some mobile devices (385px x 916px)

    @media (--vs) {
      margin-top: -1.5rem;
      padding: 0rem var(--base-component-padding-inline) 3rem;
    }
    @media (--vl) {
      margin-top: -1rem;
      padding: 0rem var(--base-component-padding-inline) 6rem;
      align-items: end;
    }

    @media (--vl) {
      row-gap: 5rem;
    }

    @media (--vs) {
      //margin-bottom: 2rem;
    }

    //@media (--vl) {
    //  grid-area: 2 / 1 / 3 / 2;
    //  display: flex;
    //  flex-direction: column;
    //  justify-content: flex-end;
    //  grid-row: 2;
    //  grid-column: 1;
    //}

    > .row {
      > .links {
        display: inline-flex;
        flex-direction: column;

        @media (--vs) {
          margin-bottom: 2rem;
        }

        > .link {
          @include text-md(700);

          width: fit-content;
          display: inline;
          position: relative;
          text-decoration: none;

          &.email,
          &.videocall {
            display: inline-block;
            margin-top: 1rem;
          }

          &::before {
            content: '';
            background: currentColor;
            height: 0.05em;
            min-height: 1px;
            bottom: -0.05em;
            left: 0;
            width: 100%;
            position: absolute;
            transform: scaleX(1);
            transform-origin: left;
            transition: transform 0.55s cubic-bezier(0.785, 0.135, 0.15, 0.86);
            transition-delay: 0.5s;
          }

          &::after {
            content: '';
            background: currentColor;
            height: 0.05em;
            min-height: 1px;
            bottom: -0.05em;
            left: 0;
            position: absolute;
            width: 100%;
            transform: scaleX(0);
            transform-origin: right;
            transition: transform 0.55s cubic-bezier(0.785, 0.135, 0.15, 0.86);
            transition-delay: 0s;
          }

          @media (hover: hover) {
            &:hover,
            &.-hover {
              &::before {
                transform: scaleX(0);
                transform-origin: right;
                transition: transform 0.55s cubic-bezier(0.785, 0.135, 0.15, 0.86);
                transition-delay: 0s;
              }

              &::after {
                transform: scaleX(1);
                transform-origin: left;
                transition: transform 0.55s cubic-bezier(0.785, 0.135, 0.15, 0.86);
                transition-delay: 0.2s;
              }
            }
          }

          &:focus-within,
          &.-focus {
            outline: 1px solid var(--color-red);
            outline-offset: 1px;
          }
        }
      }

      > .portrait {
        --width: auto;
        --height: auto;

        &.-vs {
          aspect-ratio: 4 / 5;

          @media (--vs) {
          }
          @media (--vl) {
            display: none;
          }
        }

        &.-vl {
          aspect-ratio: 4 / 2;

          @media (--vs) {
            display: none;
          }

          @media (--vl) {
          }
        }

        > :deep(.image) {
          object-fit: cover;
        }
      }
    }
  }
}
</style>
